import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Loader2, Edit,} from "lucide-react";
import MultiSelectDropdown from "../Common/MultiSelectDropdown";
import { useSuperAdmin } from "../../context/SuperAdminContext"; // ⚠️ adjust path to match your project

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getToken = () => localStorage.getItem("superadmintoken");
const getHeaders = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const COLOR = "#3B82F6";

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeLocation = (loc) => ({ _id: loc._id || loc.id, name: loc.name });
const normalizeCompany = (comp) => ({ _id: comp._id || comp.id, name: comp.name , companyCode:comp.companyCode });
const normalizeSkill = (s) => ({ id: String(s._id || s.id), name: s.name });
const normalizeShift = (s) => ({ id: String(s._id || s.id), name: s.shiftName || s.name });

const normalizeDepartment = (dept) => ({
  id: dept._id || dept.id,
  rolename: dept.department || dept.departmentName || dept.rolename || dept.name || "",
  companyName: dept.companyName || "",
  hourlyRate: dept.hourlyRate != null && dept.hourlyRate !== "" ? String(dept.hourlyRate) : "",
  incentiveRate: dept.incentiveRate != null && dept.incentiveRate !== "" ? String(dept.incentiveRate) : "",
  skillNames: (dept.skills || []).filter(s => typeof s === "string"),
  shiftNames: (dept.shifts || []).map(s => typeof s === "object" ? s.shiftName : s).filter(Boolean),
  status: dept.isActive !== undefined ? dept.isActive : true,
   masterSubDepartmentId: dept.masterSubDepartmentId
    ? String(dept.masterSubDepartmentId._id || dept.masterSubDepartmentId.id || dept.masterSubDepartmentId)
    : "",
  masterSubDepartmentName: dept.masterSubDepartmentName || "",
});

// Master department — actual API shape:
// { _id, department, skills: ["developer","coding"], skillIds: ["69b7...", "69b3..."], ... }
// `skillIds` is already a flat array of skill ObjectId strings, so no further resolution needed.
const normalizeMasterDepartment = (md) => ({
  id: String(md._id || md.id),
  name: md.department || md.name || md.departmentName || "",
  skillIds: (md.skillIds || []).map((id) => String(id)),
});

const normalizeWageHistory = (entry) => ({
  _id: entry._id,  // ✅ Preserve the _id
  oldrate: String(entry.oldPrice || ""),
  hourlyRate: String(entry.newPrice || ""),
  setBy: entry.setBy || "",
  appliedFrom: entry.appliedFrom || "",
  createdAt: entry.created_at || "",
});

const normalizeIncentiveHistory = (entry) => ({
  _id: entry._id,  // ✅ Preserve the _id
  oldrate: entry.oldIncentive != null ? String(entry.oldIncentive) : "",
  newrate: entry.newIncentive != null ? String(entry.newIncentive) : "",
  setBy: entry.setBy || "",
  appliedFrom: entry.appliedFrom || "",
  createdAt: entry.created_at || "",
});

const EMPTY_FORM = {
  departmentName: "",
  hourlyRate: "",
  incentiveRate: "",
  skillIds: [],
  shiftIds: [],
  isActive: true,
  appliedFromHourlyRate: "",
  appliedFromIncentiveRate: "",
  masterDepartmentId: "",
};

// ── Chevron SVG ────────────────────────────────────────────────────────────────
const ChevronSVG = ({ open }) => (
  <svg
    className={`h-4 w-4 transform transition-transform ${open ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);



// ── Wage History Modal ─────────────────────────────────────────────────────────
function WageHistoryModal({ department, onClose }) {
  const [history, setHistory] = useState([]);
  const [incentiveHistory, setIncentiveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentcurrentprice, setdepartmentcurrentprice] = useState("");
  const [deleting, setDeleting] = useState({});

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  
  const formatDateandtime = (iso) =>
    new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });

  const isDeleteDisabled = (appliedFrom) => {
    if (!appliedFrom) return true;
    const appliedDate = new Date(appliedFrom);
    const today = new Date();
    appliedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return appliedDate <= today;
  };

  const handleDelete = async (id, type) => {

    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      const res = await axios.post(
        `${BASE_URL}/OnDemandCompanies/delete-price-history`,
        {
          id: id,
          table: type
        },
        { headers: getHeaders() }
      );

      if (res?.data?.success === true) {
        toast.success(res?.data?.message || `${type === "price" ? "Wage" : "Incentive"} history record deleted successfully`);
        
        if (type === "price") {
          setHistory(prev => prev.filter(item => item._id !== id));
        } else {
          setIncentiveHistory(prev => prev.filter(item => item._id !== id));
        }
      } else {
        const errorMessage = res?.data?.message || `Failed to delete ${type} history record`;
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete history record");
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.post(
          `${BASE_URL}/onDemandCompanies/price-history`,
          {
            entityType: 'department',
            entityId: department.id
          },
          { headers: getHeaders() }
        );
        if (!cancelled) {
          if (res?.data?.success) {
            setdepartmentcurrentprice(res?.data?.currentPrice);
            const list = Array.isArray(res?.data?.history) ? res?.data?.history : [];
            setHistory(list.map(normalizeWageHistory));
            const incList = Array.isArray(res?.data?.incentiveHistory) ? res?.data?.incentiveHistory : [];
            setIncentiveHistory(incList.map(normalizeIncentiveHistory));
          } else {
            toast.error(res?.data?.message || "Failed to load wage history");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load wage history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [department.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Department Wage History
            </h3>
            <p className="text-sm mt-1">
              <span className="font-medium text-gray-800">Department Name:</span>{" "}
              <span className="text-gray-600">{department.rolename}</span>
            </p>
            <p className="text-sm mt-1">
              <span className="font-medium text-gray-800">Current Price:</span>{" "}
              <span className="text-gray-600 font-semibold">
                {departmentcurrentprice ? `Rs ${departmentcurrentprice}` : "-"}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : (
          <>
            {/* Hourly Rate History */}
            <h4 className="text-sm font-bold text-gray-800 mb-2">Hourly Rate History</h4>
            {history.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm mb-4">No hourly rate history found.</div>
            ) : (
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-white shadow-md z-[50]">
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">New price</th>
                      <th className="p-2 text-left">Old price</th>
                      <th className="p-2 text-left">Set By</th>
                      <th className="p-2 text-center">Done at</th>
                      <th className="p-2 text-center">Applied from</th>
                      <th className="p-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const disabled = isDeleteDisabled(h.appliedFrom);
                      return (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-2 text-left">Rs {h.hourlyRate}</td>
                          <td className="p-2 text-center">
                            {h.oldrate ? `Rs ${h.oldrate}` : "-"}
                          </td>
                          <td className="p-2 text-left">{h.setBy}</td>
                          <td className="p-2 text-center">
                            {h.createdAt ? formatDateandtime(h.createdAt) : "—"}
                          </td>
                          <td className="p-2 text-center">
                            {h.appliedFrom ? formatDate(h.appliedFrom) : "—"}
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => handleDelete(h._id, "price")}
                              disabled={disabled || deleting[h._id]}
                              className={`p-1 rounded-md transition-colors ${
                                disabled || deleting[h._id]
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-red-500 hover:bg-red-50 hover:text-red-600"
                              }`}
                              title={disabled ? "Cannot delete past or current applied date" : "Delete this record"}
                            >
                              {deleting[h._id] ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Incentive Rate History */}
            <h4 className="text-sm font-bold text-gray-800 mb-2">Incentive Rate History</h4>
            {incentiveHistory.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No incentive rate history found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-white shadow-md z-[50]">
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">New incentive</th>
                      <th className="p-2 text-left">Old incentive</th>
                      <th className="p-2 text-left">Set By</th>
                      <th className="p-2 text-center">Done at</th>
                      <th className="p-2 text-center">Applied from</th>
                      <th className="p-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incentiveHistory.map((h, i) => {
                      const disabled = isDeleteDisabled(h.appliedFrom);
                      return (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-2 text-left">Rs {h.newrate}</td>
                          <td className="p-2 text-center">
                            {h.oldrate ? `Rs ${h.oldrate}` : "-"}
                          </td>
                          <td className="p-2 text-left">{h.setBy}</td>
                          <td className="p-2 text-center">
                            {h.createdAt ? formatDateandtime(h.createdAt) : "—"}
                          </td>
                          <td className="p-2 text-center">
                            {h.appliedFrom ? formatDate(h.appliedFrom) : "—"}
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => handleDelete(h._id, "incentive")}
                              disabled={disabled || deleting[h._id]}
                              className={`p-1 rounded-md transition-colors ${
                                disabled || deleting[h._id]
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-red-500 hover:bg-red-50 hover:text-red-600"
                              }`}
                              title={disabled ? "Cannot delete past or current applied date" : "Delete this record"}
                            >
                              {deleting[h._id] ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Department Modal (Add / Edit) ──────────────────────────────────────────────
function DepartmentModal({ mode, initial, onClose, onSave, companyId }) {
  const { list_master_subdepartment } = useSuperAdmin();

  const [form, setForm] = useState(
    initial
      ? {
        departmentName: initial.rolename || "",
        hourlyRate: initial.hourlyRate || "",
        incentiveRate: initial.incentiveRate || "",
        // store selected IDs — will be resolved after options load
        skillIds: [],
        shiftIds: [],
        // temporarily hold names to resolve once options are fetched
        _skillNames: initial.skillNames || [],
        _shiftNames: initial.shiftNames || [],
        isActive: initial.status !== undefined ? initial.status : true,
        appliedFromHourlyRate: "",
        appliedFromIncentiveRate: "",
        masterDepartmentId: initial.masterSubDepartmentId || "",
      }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [skills, setSkills] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [shiftsLoading, setShiftsLoading] = useState(true);

  // ── Master departments
  const [masterDepartments, setMasterDepartments] = useState([]);
  const [masterDeptLoading, setMasterDeptLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setMasterDeptLoading(true);
        // list_master_department returns the full axios response and takes
        // an isActive status arg — pass true to fetch only active master departments.
        const res = await list_master_subdepartment(true);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!cancelled) {
          if (res?.data?.success) {
            setMasterDepartments(list.map(normalizeMasterDepartment));
          } else {
            toast.error(res?.data?.message || "Failed to load submaster departments");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.message || err?.message || "Failed to load submaster departments");
      } finally {
        if (!cancelled) setMasterDeptLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [list_master_subdepartment]);

  // Once skills load, resolve names → IDs for preselection
  useEffect(() => {
    if (!skillsLoading && skills.length > 0 && form._skillNames?.length > 0) {
      const matched = skills
        .filter(s => form._skillNames.includes(s.name))
        .map(s => s.id);
      setForm(p => ({ ...p, skillIds: matched, _skillNames: [] }));
    }
  }, [skillsLoading, skills]);

  // Once shifts load, resolve names → IDs for preselection  
  useEffect(() => {
    if (!shiftsLoading && shifts.length > 0 && form._shiftNames?.length > 0) {
      const matched = shifts
        .filter(s => form._shiftNames.includes(s.name))
        .map(s => s.id);
      setForm(p => ({ ...p, shiftIds: matched, _shiftNames: [] }));
    }
  }, [shiftsLoading, shifts]);

  // Fetch skills
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/lenskart/skills`, { headers: getHeaders() });
        if (!cancelled) {
          if (res?.data?.success) {
            setSkills((Array.isArray(res?.data?.skills) ? res?.data?.skills : []).map(normalizeSkill));
          } else {
            toast.error(res?.data?.message || "Failed to load skills");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load skills");
      } finally {
        if (!cancelled) setSkillsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Fetch shifts for this company
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/lenskart/shifts`, {
          headers: getHeaders(),
          params: { companyId, isActive: true },
        });
        if (!cancelled) {
          if (res?.data?.success) {
            setShifts((Array.isArray(res?.data?.data) ? res?.data?.data : []).map(normalizeShift));
          } else {
            toast.error(res?.data?.message || "Failed to load shifts");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load shifts");
      } finally {
        if (!cancelled) setShiftsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [companyId]);

  const originalHourlyRate = initial?.hourlyRate || "";
  const originalIncentiveRate = initial?.incentiveRate || "";
  const hourlyRateChanged = mode === "edit" && form.hourlyRate !== originalHourlyRate;
  const incentiveRateChanged = mode === "edit" && form.incentiveRate !== originalIncentiveRate;

  // Currently selected master department object (if any)
  const selectedMasterDept = masterDepartments.find((m) => m.id === form.masterDepartmentId);

  // When a master department is selected, skill options are restricted to its
  // skill set — this blocks adding any skill outside that set, while still
  // allowing already-selected skills to be unchecked/removed via `selected`.
  const skillOptions = selectedMasterDept
    ? skills.filter((s) => selectedMasterDept.skillIds.includes(s.id))
    : skills;

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  // Selecting a master department auto-selects all of its related skills.
  // Selecting "none" restores the full skill list and clears the auto-picks.
  const handleMasterDepartmentChange = (mdId) => {
    const md = masterDepartments.find((m) => m.id === mdId);
    const matchedSkillIds = md
      ? skills.filter((s) => md.skillIds.includes(s.id)).map((s) => s.id)
      : [];

    setForm((p) => ({
      ...p,
      masterDepartmentId: mdId,
      skillIds: matchedSkillIds,
    }));
    setErrors((p) => ({ ...p, masterDepartmentId: "", skillIds: "" }));
  };

  const validate = () => {
    const e = {};
    if (!String(form.departmentName || "").trim()) e.departmentName = "Department name is required";
    else if (!form.masterDepartmentId) e.masterDepartmentId = "SubMaster department is required";
    else if (!form.skillIds.length) e.skillIds = "Select at least one skill";
    else if (!form.shiftIds.length) e.shiftIds = "Select at least one shift";
    else if (hourlyRateChanged && !form.appliedFromHourlyRate) e.appliedFromHourlyRate = "Hourly rate applied from date is required";
    else if (incentiveRateChanged && !form.appliedFromIncentiveRate) e.appliedFromIncentiveRate = "Incentive applied from date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res =
        mode === "add"
          ? await axios.post(
            `${BASE_URL}/OnDemandCompanies/add-new-department`,
            {
              companyId,
              department: form.departmentName,
              skillIds: form.skillIds,
              shiftIds: form.shiftIds,
              HourlyRate: Number(form.hourlyRate),
              incentiveRate: Number(form.incentiveRate),
              isActive: form.isActive,
              masterSubDepartmentId: form.masterDepartmentId || null,
            },
            { headers: getHeaders() }
          )
          : await axios.put(
            `${BASE_URL}/OnDemandCompanies/edit-department/${initial.id}`,
            {
              departmentName: form.departmentName,
              masterSubDepartmentId: form.masterDepartmentId,
              skillIds: form.skillIds,
              shiftIds: form.shiftIds,
              hourlyRate: Number(form.hourlyRate),
              incentiveRate: Number(form.incentiveRate),
              isActive: form.isActive,
              ...(hourlyRateChanged && form.appliedFromHourlyRate ? { appliedFromHourlyRate: form.appliedFromHourlyRate } : {}),
              ...(incentiveRateChanged && form.appliedFromIncentiveRate ? { appliedFromIncentiveRate: form.appliedFromIncentiveRate } : {}),
            },
            { headers: getHeaders() }
          );

      if (res?.data?.success) {
        toast.success(
          mode === "add" ? "Department added successfully" : "Department updated successfully"
        );
        onSave(normalizeDepartment(res?.data?.data || {}));
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {mode === "add" ? "Add New Department" : "Edit Department"}
          </h3>
          <button onClick={onClose} disabled={submitting} className="p-1.5 rounded-md hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Department Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.departmentName}
              onChange={(e) => set("departmentName", e.target.value)}
              placeholder="e.g. Scanning"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.departmentName ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
            />
            {errors.departmentName && (
              <p className="text-red-500 text-xs mt-1">{errors.departmentName}</p>
            )}
          </div>

          {/* Master Department — shown in both add and edit modes, and
              compulsory. Selecting one auto-picks its skills and restricts
              the Skills dropdown below to that set. The placeholder option
              and unselected select text are styled text-gray-400. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SubMaster Department <span className="text-red-500">*</span>
            </label>
            <select
              value={form.masterDepartmentId}
              onChange={(e) => handleMasterDepartmentChange(e.target.value)}
              disabled={masterDeptLoading}
              className={`w-full h-7 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white disabled:opacity-60 ${
                errors.masterDepartmentId ? "border-red-400 bg-red-50" : "border-gray-300"
              } ${form.masterDepartmentId ? "text-gray-800" : "text-gray-400"}`}
            >
              <option value="" className="text-gray-400">
                {masterDeptLoading ? "Loading master departments..." : "Select submaster department"}
              </option>
              {masterDepartments.map((md) => (
                <option key={md.id} value={md.id} className="text-gray-800">{md.name}</option>
              ))}
            </select>
            {errors.masterDepartmentId && (
              <p className="text-red-500 text-xs mt-1">{errors.masterDepartmentId}</p>
            )}
          </div>

          {/* Hourly Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hourly Rate 
            </label>
            <input
              type="number"
              min="0"
                onKeyDown={(e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  }}
              value={form.hourlyRate}
              onChange={(e) => set("hourlyRate", e.target.value)}
              placeholder="e.g. 150"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 "
                }`}
            />

          </div>

          {/* Hourly Rate Applied From — only in edit mode when hourly rate changed */}
          {hourlyRateChanged && (
            <div className="rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate Applied From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.appliedFromHourlyRate}
                onChange={(e) => set("appliedFromHourlyRate", e.target.value)}
                className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.appliedFromHourlyRate ? "border-red-400 bg-red-50" : " bg-white"
                  }`}
              />
              {errors.appliedFromHourlyRate && (
                <p className="text-red-500 text-xs mt-1">{errors.appliedFromHourlyRate}</p>
              )}
            </div>
          )}

          {/* Incentive Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Incentive Rate
            </label>
            <input
              type="number"
              min="0"
                onKeyDown={(e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  }}
              value={form.incentiveRate}
              onChange={(e) => set("incentiveRate", e.target.value)}
              placeholder="e.g. 20"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 "
                }`}
            />

          </div>

          {/* Incentive Applied From — only in edit mode when incentive rate changed */}
          {incentiveRateChanged && (
            <div className="rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Incentive Applied From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.appliedFromIncentiveRate}
                onChange={(e) => set("appliedFromIncentiveRate", e.target.value)}
                className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.appliedFromIncentiveRate ? "border-red-400 bg-red-50" : " bg-white"
                  }`}
              />
              {errors.appliedFromIncentiveRate && (
                <p className="text-red-500 text-xs mt-1">{errors.appliedFromIncentiveRate}</p>
              )}
            </div>
          )}

          {/* Skills — restricted to the selected master department's skill set
              when one is chosen (see skillOptions above). Already-selected
              skills remain visible/removable even if this list narrows. */}
          <MultiSelectDropdown
            label="Skills"
            options={skillOptions}
            selected={form.skillIds}
            onChange={(val) => set("skillIds", val)}
            error={errors.skillIds}
            placeholder="Select skills"
            loading={skillsLoading}
          />

          {/* Shifts */}
          <MultiSelectDropdown
            label="Shifts"
            options={shifts}
            selected={form.shiftIds}
            onChange={(val) => set("shiftIds", val)}
            error={errors.shiftIds}
            placeholder="Select shifts"
            loading={shiftsLoading}
          />

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set("isActive", true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${form.isActive
                    ? "bg-green-100 border-green-400 text-green-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-green-300"
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${form.isActive ? "bg-green-600" : "bg-gray-300"}`} />
                Active
              </button>
              <button
                type="button"
                onClick={() => set("isActive", false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${!form.isActive
                    ? "bg-red-100 border-red-400 text-red-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-red-300"
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${!form.isActive ? "bg-red-600" : "bg-gray-300"}`} />
                Inactive
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg hover:opacity-90 bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === "add" ? "Add Department" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ManageRoles() {
  // ── Data
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  // ── Selection
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  // ── Loading
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [togglingDeptId, setTogglingDeptId] = useState(null);

  // ── Dropdown open
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  // ── Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [wageHistoryDept, setWageHistoryDept] = useState(null);

  // ── Load locations on mount
  const loadLocations = useCallback(async () => {
    try {
      setLocationsLoading(true);
      const res = await axios.get(`${BASE_URL}/lenskart/locations`, { headers: getHeaders() });
      if (res?.data?.success && Array.isArray(res?.data?.locations)) {
        setLocations(res?.data?.locations.map(normalizeLocation));
      } else {
        toast.error(res?.data?.message || "Failed to load locations");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load locations");
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  // ── Location selected → load companies
  const handleLocationChange = useCallback(async (locId) => {
    setSelectedLocation(locId);
    setSelectedCompany("");
    setDepartments([]);
    setCompanies([]);
    setIsLocationOpen(false);
    try {
      setCompaniesLoading(true);
      const res = await axios.get(
        `${BASE_URL}/OnDemandCompanies/list-companies/${locId}`,
        {
          headers: getHeaders(),
          params: {isActive: true },
        }
      );
      if (res?.data && Array.isArray(res?.data?.companies)) {
        setCompanies(res?.data?.companies.map(normalizeCompany));
      } else {
        toast.error(res?.data?.message || "Failed to load companies");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load companies");
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  // ── Company selected → load departments
  const loadDepartments = useCallback(async (companyId) => {
    try {
      setDepartmentsLoading(true);
      const res = await axios.post(`${BASE_URL}/OnDemandCompanies/list-departments`,
        {
          companyId, isActive: false 
        } ,{
        headers: getHeaders(),
      });
      if (res?.data?.success && Array.isArray(res?.data?.departments)) {
        console.log("RAW dept[0]:", JSON.stringify(res?.data?.departments[0], null, 2)); // ← ADD THIS
        setDepartments(res?.data?.departments.map(normalizeDepartment));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load departments");
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  const handleCompanyChange = useCallback(async (companyId) => {
    setSelectedCompany(companyId);
    setDepartments([]);
    setIsCompanyOpen(false);
    await loadDepartments(companyId);
  }, [loadDepartments]);

  // ── Toggle status
  const handleToggle = async (dept) => {
    setTogglingDeptId(dept.id);
    try {
      const res = await axios.put(
        `${BASE_URL}/OnDemandCompanies/edit-department/${dept.id}`,
        { isActive: !dept.status },
        { headers: getHeaders() }
      );
      if (res?.data?.success) {
        toast.success(`Department marked as ${dept.status ? "Inactive" : "Active"}`);
        setDepartments((prev) =>
          prev.map((d) => (d.id === dept.id ? { ...d, status: !d.status } : d))
        );
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setTogglingDeptId(null);
    }
  };

  // ── Add / Edit callbacks
  // ── Add / Edit callbacks
  const handleAdd = useCallback(() => {
    if (selectedCompany) loadDepartments(selectedCompany);
  }, [selectedCompany, loadDepartments]);
  const handleEdit = useCallback(() => {
    if (selectedCompany) loadDepartments(selectedCompany);
  }, [selectedCompany, loadDepartments]);

  const locationName = selectedLocation
    ? locations.find((l) => l._id === selectedLocation)?.name || "Select Location"
    : "Select Location";

const companyName = selectedCompany
  ? (() => {
      const comp = companies.find((c) => c._id === selectedCompany);
      return comp
        ? `${comp.name}${comp.companyCode ? ` (${comp.companyCode})` : ""}`
        : "Select Company";
    })()
  : "Select Company";

  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Departments</h2>
          <p className="text-s text-gray-400">
            {departmentsLoading ? "Loading..." : selectedCompany ? `Total Departments : ${departments.length}` : ""}
          </p>
        </div>
        {selectedCompany && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
          >
            Add Department
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        {/* Location */}
        <div className="relative flex-1">
          <button
            onClick={() => { if (!locationsLoading) setIsLocationOpen((p) => !p); }}
            disabled={locationsLoading}
            className={`w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 flex justify-between items-center ${locationsLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              }`}
          >
            <span className={locationName === "Select Location" ? "text-gray-400" : "text-gray-800"}>
              {locationsLoading ? "Loading locations..." : locationName}
            </span>
            {locationsLoading
              ? <Loader2 size={13} className="animate-spin text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
              : <ChevronSVG open={isLocationOpen} />}
          </button>
          {isLocationOpen && !locationsLoading && (
            <div className="absolute z-[200] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-200">
              {locations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No locations found</div>
              ) : (
                locations.map((loc) => (
                  <button
                    key={loc._id}
                    onClick={() => handleLocationChange(loc._id)}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === loc._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                      }`}
                  >
                    {loc.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Company */}
        <div className="relative flex-1">
          <button
            onClick={() => { if (selectedLocation && !companiesLoading) setIsCompanyOpen((p) => !p); }}
            disabled={!selectedLocation || companiesLoading}
            className={`w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 flex justify-between items-center ${!selectedLocation || companiesLoading ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white cursor-pointer"
              }`}
          >
            <span className={companyName === "Select Company" ? "text-gray-400" : "text-gray-800"}>
              {companiesLoading ? "Loading companies..." : companyName}
            </span>
            {companiesLoading
              ? <Loader2 size={13} className="animate-spin text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
              : <ChevronSVG open={isCompanyOpen} />}
          </button>
          {isCompanyOpen && selectedLocation && !companiesLoading && (
            <div className="absolute z-[200] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-200">
              {companies.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No companies found</div>
              ) : (
                companies.map((company) => (
                  <button
                    key={company._id}
                    onClick={() => handleCompanyChange(company._id)}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                      }`}
                  >
                    {company.name}{company.companyCode && ` (${company.companyCode})`}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="w-full">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white shadow-md z-[50]">
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Department Name</th>
                 <th className="p-2 text-center">SubMaster Department Name</th>
                <th className="p-2 text-center">Hourly Rate</th>
                <th className="p-2 text-center">Incentive Rate</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Wages History</th>
                <th className="p-2 text-center">Edit</th>

              </tr>
            </thead>
            <tbody>
              {!selectedCompany ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <p className="text-sm text-gray-400">
                      {!selectedLocation ? "Select a location to get started" : "Select a company to view departments"}
                    </p>
                  </td>
                </tr>
              ) : departmentsLoading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <Loader2 size={28} className="animate-spin mx-auto mb-3 text-gray-400" />
                    <p className="text-sm text-gray-400">Loading departments...</p>
                  </td>
                </tr>
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <p className="text-sm text-gray-500 font-medium">No departments found.</p>
                  </td>
                </tr>
              ) : (
                departments.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className={`font-medium ${row.status ? "text-gray-800" : "text-gray-400"}`}>{row.rolename}</td>
                   <td className="p-3 text-center font-semibold text-gray-800">
   {row.masterSubDepartmentName || "-"}
</td>
                    <td className="p-3 text-center font-semibold text-gray-800">
                      {row.hourlyRate ? `Rs ${row.hourlyRate}` : "—"}
                    </td>
                    <td className="p-3 text-center font-semibold text-gray-800">
                      {row.incentiveRate ? `Rs ${row.incentiveRate}` : "—"}
                    </td>
                    <td className="p-2 text-center">
                      <span
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold w-fit mx-auto"
                        style={{ background: row.status ? "#DCFCE7" : "#FEE2E2", color: row.status ? "#16A34A" : "#DC2626" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.status ? "#16A34A" : "#DC2626" }} />
                        {row.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <span
                        onClick={() => setWageHistoryDept(row)}
                        className="text-xs font-semibold underline cursor-pointer"
                        title="View wage history"
                      >
                        Show
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => setEditDept(row)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-all mx-auto block"
                        title="Edit department"
                      >
                        <Edit size={14} color={COLOR} />
                      </button>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <DepartmentModal mode="add" companyId={selectedCompany} onClose={() => setShowAddModal(false)} onSave={handleAdd} />
      )}
      {editDept && (
        <DepartmentModal mode="edit" initial={editDept} companyId={selectedCompany} onClose={() => setEditDept(null)} onSave={(saved) => handleEdit({ ...saved, id: editDept.id })} />
      )}
      {wageHistoryDept && (
        <WageHistoryModal department={wageHistoryDept} onClose={() => setWageHistoryDept(null)} />
      )}

    </div>
  );
}